/**
 * Dónde está la cita dentro del crudo.
 *
 * Es la MISMA definición de «está sustentado» que usa el backend para decidir si un hecho entra
 * solo o va a la cola (`agent_tools/services/fact_grounding.py`, `GROUNDING_VERSION = 2`):
 * minúsculas sin acentos por descomposición Unicode, separador de miles quitado, cualquier corrida
 * de puntuación o espacios colapsada a UN espacio, y la cita buscada entre bordes de palabra. Con
 * una diferencia: además de decir SI la cita está, dice DÓNDE — los offsets sobre el texto
 * ORIGINAL — para que el visor pueda resaltarla y llevar hasta ahí.
 *
 * Que las dos definiciones sean la misma es lo que importa: lo que el gate dio por sustentado se
 * encuentra acá, y lo que acá no aparece es exactamente lo que el gate mandó a revisar. Si el
 * backend cambia de versión, este módulo tiene que cambiar con él (ver `GROUNDING_VERSION`).
 *
 * Todo es puro y sin dependencias: se testea con strings.
 */

// Espejo de `fact_grounding.GROUNDING_VERSION`. Se deja escrito para que un cambio de definición
// del lado del backend tenga un lugar obvio que actualizar acá.
export const GROUNDING_VERSION = 2;

/**
 * Una cita de un solo token («20», «lancome») no SUSTENTA nada por sí sola — mismos umbrales que
 * `fact_grounding.MIN_QUOTE_TOKENS` / `MIN_QUOTE_CHARS`.
 *
 * 🔴 Es el umbral **probatorio**, y por eso vive sólo en `quoteGrounded`, NO en `locateQuote`.
 * Hasta el 2026-09-13 lo aplicaban los dos, y eso hacía que el visor mintiera en pantalla: con el
 * crudo «…acordado es $1.250.000 para el Q3…», `locateQuote('$1.250.000')` devolvía `[]` sin
 * siquiera buscar, y la barra afirmaba «La cita no aparece textual en este material» sobre una cita
 * que estaba ahí, palabra por palabra, a la vista del usuario (review C-cita-01).
 *
 * Son dos preguntas distintas y hay que contestarlas por separado: «¿esta cita alcanza como
 * evidencia?» (el gate del backend) y «¿dónde está esta cadena en este texto?» (el visor). Para
 * resaltar, encontrar «$1.250.000» es útil y es verdad.
 */
export const MIN_QUOTE_TOKENS = 2;
export const MIN_QUOTE_CHARS = 12;

/**
 * Prefijos de un crudo que NO es texto: es el marcador que dejó la fuente.
 *
 * Espeja `fact_grounding.UNGROUNDABLE_PREFIXES`, que existe para devolver `None` («no se puede
 * afirmar ni negar») en vez de `False` cuando no hay contra qué verificar. El visor necesita la
 * misma distinción: sobre un PDF escaneado, decir «la cita no aparece textual» y sugerir Ctrl+F es
 * falso y además inútil — no hay texto donde buscar (QA-VIS-cita-01).
 *
 * ⚠️ **Es un SUPERCONJUNTO del backend, a propósito**: se le suma `[pdf:`, que `material_parsers`
 * genera de verdad (`'[PDF: %s]'`, fail-open cuando `pypdf` no puede abrir el archivo) y que la
 * lista del backend no contempla. Esa omisión es del backend y está fichada aparte; acá no se
 * puede esperar a que se arregle, porque es justo el caso que se ve en pantalla.
 */
export const UNGROUNDABLE_PREFIXES = ['[imagen:', '[audio:', '[drive]', '[upload]', '[pdf:'];

/** ¿El crudo es un marcador de la fuente (o está vacío) en vez de texto de verdad? */
export function textIsUngroundable(text) {
    const plain = (text === null || text === undefined ? '' : String(text)).trim();
    if (!plain) return true;
    const lower = plain.toLowerCase();
    return UNGROUNDABLE_PREFIXES.some(prefix => lower.startsWith(prefix));
}

const isDigit = (ch) => ch >= '0' && ch <= '9';
const isAlnum = (ch) => (ch >= '0' && ch <= '9') || (ch >= 'a' && ch <= 'z');
// Marcas combinantes (tildes, diéresis, cedillas descompuestas): `unicodedata.combining` del lado
// de Python. La categoría M las cubre todas.
const COMBINING = /\p{M}/u;

function codePointWidthAt(text, index) {
    const cp = text.codePointAt(index);
    return cp > 0xFFFF ? 2 : 1;
}

/**
 * Paso 1 del backend: NFKD + descartar las marcas combinantes, en minúsculas.
 *
 * Devuelve un carácter por posición, con el índice del texto ORIGINAL que lo produjo. Va separado
 * del paso 2 porque el ORDEN importa y es lo que hace fiel al espejo: el backend quita el separador
 * de miles **después** de descomponer (`_THOUSANDS.sub` corre sobre `plain`), así que un dígito de
 * ancho completo —«１.０００», que NFKD convierte en «1.000»— también pierde el separador. Mientras
 * esto se decidía sobre el texto crudo, el JS veía «１» y su `isDigit` ASCII decía que no, y las dos
 * implementaciones divergían (review C-cita-06: 82 divergencias en un fuzz de 8.000 strings, todas
 * de esta familia).
 */
function decomposeWithMap(source) {
    const chars = [];
    const map = [];
    for (let i = 0; i < source.length;) {
        const width = codePointWidthAt(source, i);
        const ch = source.substr(i, width);
        if (source.charCodeAt(i) < 128) {
            // Atajo ASCII: el 95 % de un crudo en español, y el ASCII ya es estable bajo NFKD.
            chars.push(ch.toLowerCase());
            map.push(i);
        } else {
            for (const piece of ch.normalize('NFKD')) {
                if (COMBINING.test(piece)) continue;
                for (const lowered of piece.toLowerCase()) {
                    chars.push(lowered);
                    map.push(i);
                }
            }
        }
        i += width;
    }
    return {chars, map};
}

/**
 * Normaliza `text` como el backend y devuelve, además, el mapa de vuelta: `map[i]` es el índice
 * en `text` del carácter que produjo `normalized[i]` (para un espacio, el del primer separador
 * que lo generó). Sin lookbehind en las regex a propósito: Safari lo soporta recién desde 16.4.
 */
export function normalizeWithMap(text) {
    const source = text === null || text === undefined ? '' : String(text);
    const {chars, map} = decomposeWithMap(source);
    const out = [];
    const outMap = [];
    let pendingSpace = false;
    let pendingSpaceAt = -1;

    for (let k = 0; k < chars.length; k += 1) {
        const ch = chars[k];
        // «10.000» es UN token: el separador de miles (o el decimal) entre dos dígitos se quita, de
        // los dos lados por igual, así que sigue matcheando. Se decide sobre el texto YA
        // descompuesto, como el backend — ver `decomposeWithMap`.
        if ((ch === '.' || ch === ',')
            && k > 0 && isDigit(chars[k - 1])
            && k + 1 < chars.length && isDigit(chars[k + 1])) {
            continue;
        }
        if (isAlnum(ch)) {
            if (pendingSpace && out.length > 0) {
                out.push(' ');
                outMap.push(pendingSpaceAt);
            }
            pendingSpace = false;
            out.push(ch);
            outMap.push(map[k]);
        } else {
            if (!pendingSpace) pendingSpaceAt = map[k];
            pendingSpace = true;
        }
    }
    return {normalized: out.join(''), map: outMap};
}

/** Sólo el texto normalizado — `fact_grounding.normalize_for_grounding`. */
export function normalizeForGrounding(text) {
    return normalizeWithMap(text).normalized;
}

function quoteTooShort(normalizedQuote) {
    return !normalizedQuote
        || (normalizedQuote.split(' ').length < MIN_QUOTE_TOKENS
            && normalizedQuote.length < MIN_QUOTE_CHARS);
}

/**
 * Todas las apariciones de `quote` en `text`, como rangos `{start, end}` (fin exclusivo) sobre el
 * texto ORIGINAL, en orden. Vacío si la cita no está o si no hay texto. Determinístico, sin LLM.
 *
 * 🔴 **Localiza SIEMPRE**: no aplica el umbral probatorio (ver `MIN_QUOTE_TOKENS`). Una cita de un
 * token puede no alcanzar como evidencia y estar igual en el texto; resaltarla es correcto.
 *
 * ⚠️ Las apariciones que se DEVUELVEN no se solapan entre sí: el barrido avanza el largo de la
 * cita, así que «a b a» dentro de «a b a b a» cuenta una vez y no dos. Es deliberado y no un
 * descuido (review C-cita-10): el contador de la barra y los `<mark>` que se pintan salen de esta
 * misma lista, y `splitByRanges` descarta los rangos solapados — devolver dos acá daría un «1 de 2»
 * con un solo resaltado y una navegación que no llega a ningún lado.
 */
export function locateQuote(quote, text) {
    const needleText = normalizeForGrounding(quote);
    if (!needleText) return [];
    const source = text === null || text === undefined ? '' : String(text);
    const {normalized, map} = normalizeWithMap(source);
    if (!normalized) return [];

    // Bordes de palabra: «el CPA objetivo es 2» no puede darse por encontrado dentro de
    // «el CPA objetivo es 20 dólares».
    const hay = ` ${normalized} `;
    const needle = ` ${needleText} `;
    const ranges = [];
    let from = 0;
    for (;;) {
        const at = hay.indexOf(needle, from);
        if (at === -1) break;
        // `hay[k]` es `normalized[k - 1]`; la cita empieza en `hay[at + 1]`.
        const firstIndex = map[at];
        const lastIndex = map[at + needleText.length - 1];
        ranges.push({start: firstIndex, end: lastIndex + codePointWidthAt(source, lastIndex)});
        // El espacio de cierre puede ser el de apertura de la siguiente aparición.
        from = at + needle.length - 1;
    }
    return ranges;
}

/**
 * ¿La cita SUSTENTA el hecho? — el espejo de `fact_grounding.quote_grounded`.
 *
 * Acá sí corre el umbral probatorio: es la pregunta del gate, no la del visor. El visor usa
 * `locateQuote`, que localiza aunque la cita sea corta.
 */
export function quoteGrounded(quote, text) {
    if (quoteTooShort(normalizeForGrounding(quote))) return false;
    return locateQuote(quote, text).length > 0;
}

/**
 * Parte `text` en segmentos consecutivos `{text, hit}` según `ranges` (que se asumen ordenados y
 * sin solaparse, como los devuelve `locateQuote`). Es lo que el visor pinta: los `hit` van en
 * `<mark>`, el resto tal cual.
 */
export function splitByRanges(text, ranges) {
    const source = text === null || text === undefined ? '' : String(text);
    const segments = [];
    let cursor = 0;
    (ranges || []).forEach(({start, end}) => {
        if (start < cursor || end <= start) return;
        if (start > cursor) segments.push({text: source.slice(cursor, start), hit: false});
        segments.push({text: source.slice(start, end), hit: true});
        cursor = end;
    });
    if (cursor < source.length) segments.push({text: source.slice(cursor), hit: false});
    return segments;
}
